import metrics from './sf-metrics.json';

/**
 * Lock screen notification of an iPhone 17 (402×874pt) on iOS 26, in points,
 * as measured on the iOS 26.5 simulator: a lone notification sits above the
 * bottom of the screen and grows upwards; its text is laid out by the rules
 * below with SF Pro's own metrics (sf-metrics.json), so the preview breaks and
 * cuts lines exactly where the phone does whatever font the browser has.
 */
export const IOS_LOCK_SCREEN = {
  screenWidth: 402,
  screenHeight: 874,
  /** From the card's bottom edge to the bottom of the screen. */
  cardBottomInset: 128,
  cardSideInset: 14,
  cardRadius: 23,
  /** Card left edge → icon; icon is vertically centred. */
  iconInset: 14,
  iconSize: 38,
  /** Card left edge → text (icon + 12pt gap). */
  textInset: 64,
  /** Text column width; the time label's frame ends at its right edge. */
  textWidth: 293,
  titleTimeGap: 10,
  /** Baseline to baseline. */
  lineHeight: 18,
  /** Card top → title baseline. */
  titleBaseline: 77 / 3,
  /** Last baseline → card bottom. */
  bottomBaselineInset: 17,
  bodyMaxLines: 4,
} as const;

/** Height of the card for a body of `bodyLines` lines (title is one line). */
export function iosNotificationCardHeight(bodyLines: number): number {
  const { titleBaseline, lineHeight, bottomBaselineInset } = IOS_LOCK_SCREEN;
  return titleBaseline + lineHeight * bodyLines + bottomBaselineInset;
}

type TextStyle = keyof typeof metrics.styles;

const ELLIPSIS = '…';
// UIKit sizes labels to whole device pixels (@3x).
const ceilToPixel = (points: number) => Math.ceil(points * 3 - 1e-9) / 3;

const CHAR_INDEX = new Map(
  Array.from(metrics.chars).map((char, index) => [char, index])
);
const KERNING = {
  regular: buildKerning(metrics.kerning.regular),
  semibold: buildKerning(metrics.kerning.semibold),
};

function buildKerning(table: Record<string, (string | number[])[]>) {
  const pairs = new Map<string, number>();
  for (const [left, [rights, values]] of Object.entries(table)) {
    if (typeof rights !== 'string' || !Array.isArray(values)) continue;
    Array.from(rights).forEach((right, i) => {
      pairs.set(left + right, (values[i] ?? 0) / 100);
    });
  }
  return pairs;
}

// One grapheme: a flag, an emoji sequence (modifiers, VS16, ZWJ), a keycap,
// or a character with its combining marks.
const GRAPHEME =
  /\p{Regional_Indicator}{2}|\p{Extended_Pictographic}(?:\p{Emoji_Modifier}|\uFE0F|\u20E3)*(?:\u200D\p{Extended_Pictographic}(?:\p{Emoji_Modifier}|\uFE0F)*)*|[0-9#*]\uFE0F?\u20E3|\P{M}\p{M}*|\p{M}+/gu;
const EMOJI = /\p{Extended_Pictographic}|\p{Regional_Indicator}|\u20E3/u;

export function graphemes(text: string): string[] {
  return text.match(GRAPHEME) ?? [];
}

/**
 * `text` split into runs of plain text and single emoji: iOS draws emoji
 * bigger than the browser does (see the emoji advance in sf-metrics.json).
 */
export function splitIosEmoji(
  text: string
): { text: string; emoji: boolean }[] {
  const runs: { text: string; emoji: boolean }[] = [];
  for (const g of graphemes(text)) {
    const emoji = EMOJI.test(g);
    const last = runs[runs.length - 1];
    if (last && !emoji && !last.emoji) last.text += g;
    else runs.push({ text: g, emoji });
  }
  return runs;
}

/** Advance width of `text` in points, as CoreText lays it on one line. */
export function measureIosText(text: string, style: TextStyle): number {
  const { size, advances, emoji, kerning } = metrics.styles[style];
  const pairs = KERNING[kerning === 'semibold' ? 'semibold' : 'regular'];
  const kerningScale = size / 15;
  // Outside the table (rare scripts): SF's "o" is a typical lowercase width.
  const fallback = advances[CHAR_INDEX.get('o') ?? 0] / 1000;
  let width = 0;
  let previous: string | null = null;
  for (const g of graphemes(text.normalize('NFC'))) {
    const index = CHAR_INDEX.get(g);
    if (index !== undefined) {
      width += advances[index] / 1000;
      if (previous !== null) {
        width += (pairs.get(previous + g) ?? 0) * kerningScale;
      }
      previous = g;
      continue;
    }
    width += EMOJI.test(g) ? emoji : fallback;
    previous = null;
  }
  return width;
}

// Break opportunities (UAX #14, as far as notification copy goes): after
// spaces, and after a hyphen or dash that follows a letter and is not
// followed by a digit ("meia-|noite", but "10-20" stays whole).
function splitWords(paragraph: string): string[] {
  const gs = graphemes(paragraph);
  const words: string[] = [];
  let current = '';
  gs.forEach((g, i) => {
    current += g;
    const next = gs[i + 1];
    if (next === undefined) return;
    const afterSpace = /\s/u.test(g) && !/\s/u.test(next);
    const afterHyphen =
      /[-‐–—]/u.test(g) &&
      /\p{L}/u.test(gs[i - 1] ?? '') &&
      !/[\s\p{Nd}]/u.test(next);
    if (afterSpace || afterHyphen) {
      words.push(current);
      current = '';
    }
  });
  if (current) words.push(current);
  return words;
}

const fits = (text: string, style: TextStyle, width: number) =>
  measureIosText(text.trimEnd(), style) <= width;

// A word wider than the line is broken between graphemes.
function breakLongWord(word: string, width: number): string[] {
  const pieces: string[] = [];
  let piece = '';
  for (const g of graphemes(word)) {
    if (piece && !fits(piece + g, 'body', width)) {
      pieces.push(piece);
      piece = '';
    }
    piece += g;
  }
  if (piece) pieces.push(piece);
  return pieces;
}

/** Greedy lines of one paragraph, each as its list of words. */
function wrapParagraph(paragraph: string, width: number): string[][] {
  const lines: string[][] = [];
  let line: string[] = [];
  for (const word of splitWords(paragraph)) {
    if (fits([...line, word].join(''), 'body', width)) {
      line.push(word);
      continue;
    }
    if (line.length > 0) lines.push(line);
    line = [];
    const pieces = fits(word, 'body', width)
      ? [word]
      : breakLongWord(word, width);
    pieces.slice(0, -1).forEach((piece) => lines.push([piece]));
    line = [pieces[pieces.length - 1]];
  }
  if (line.length > 0) lines.push(line);

  // UIKit's line break strategy (.pushOut): never leave a single word alone
  // on the last line; the previous line gives it its last word.
  const last = lines[lines.length - 1];
  const before = lines[lines.length - 2];
  if (last?.length === 1 && before && before.length > 1) {
    const moved = [before[before.length - 1], ...last];
    if (fits(moved.join(''), 'body', width)) {
      lines[lines.length - 2] = before.slice(0, -1);
      lines[lines.length - 1] = moved;
    }
  }
  return lines;
}

/** Longest start of `text` that fits `width` with "…" after it. */
function truncate(text: string, style: TextStyle, width: number): string {
  const gs = graphemes(text);
  let best = ELLIPSIS;
  for (let n = 1; n <= gs.length; n++) {
    const candidate = gs.slice(0, n).join('').trimEnd() + ELLIPSIS;
    if (measureIosText(candidate, style) > width) break;
    best = candidate;
  }
  return best;
}

/**
 * The title as the lock screen shows it: one line, cut with "…" where it
 * would run into the time label (`timeLabel`, e.g. "agora").
 */
export function layoutIosNotificationTitle(
  title: string,
  timeLabel: string
): string {
  const { textWidth, titleTimeGap } = IOS_LOCK_SCREEN;
  const available =
    textWidth - titleTimeGap - ceilToPixel(measureIosText(timeLabel, 'time'));
  const text = title.replace(/\s*\n\s*/gu, ' ').trim();
  return fits(text, 'title', available)
    ? text
    : truncate(text, 'title', available);
}

/**
 * The body as the lock screen shows it: at most four lines, the last one cut
 * with "…" when there is more text.
 */
export function layoutIosNotificationBody(body: string): string[] {
  const { textWidth, bodyMaxLines } = IOS_LOCK_SCREEN;
  const paragraphs = body.normalize('NFC').trim().split(/\r?\n/u);
  const lines: { words: string[]; rest: string }[] = [];
  paragraphs.forEach((paragraph, p) => {
    const wrapped = wrapParagraph(paragraph, textWidth);
    wrapped.forEach((words, i) => {
      // Text from this line on, to fill a truncated last line.
      const rest =
        wrapped
          .slice(i)
          .map((w) => w.join(''))
          .join('') + (p < paragraphs.length - 1 ? '\n' : '');
      lines.push({ words, rest });
    });
    if (wrapped.length === 0) lines.push({ words: [], rest: '' });
  });

  if (lines.length <= bodyMaxLines) {
    return lines.map((line) => line.words.join('').trimEnd());
  }
  const visible = lines
    .slice(0, bodyMaxLines - 1)
    .map((line) => line.words.join('').trimEnd());
  const last = lines[bodyMaxLines - 1].rest.split('\n')[0];
  return [...visible, truncate(last, 'body', textWidth)];
}
